import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Form from "@atlaskit/form";
import { ConfluenceLogo } from "@atlaskit/logo";
import { N700, N800 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { withFeatureExposed } from "@/utils/feature-exposed";

import { InputSiteName } from "../../components";
import { useSiteNameContext } from "../../site-name-context";

export const CobrandedProductLogo = () => (
    <div css={logoContainerCss}>
        <ConfluenceLogo size="medium" appearance="brand" />
    </div>
);

export const WorkspaceNameInput = () => {
    const label = (
        <FormattedMessage
            id="BxpUiSignup.nameSite.inputWorkspaceNameLabel"
            defaultMessage="Workspace name"
        />
    );

    const inputMessage = (
        <FormattedMessage
            id="BxpUiSignup.nameSite.workspaceIputMessage"
            defaultMessage="This is just a suggestion. Feel free to change to something your team will recognize."
        />
    );

    return (
        <>
            <div css={inputCss}>
                <InputSiteName inputLabel={label} />
            </div>
            <div css={privacyPolicyCss}>{inputMessage}</div>
        </>
    );
};

export const StyledWord = (node: ReactNode, isEnglish: boolean) => {
    return (
        <span css={accentImageSpanCss}>
            {node}
            {isEnglish && (
                <svg
                    aria-hidden="true"
                    css={accentImageCss}
                    fill="#FCA700"
                    viewBox="0 0 57 52"
                >
                    <path d="M43.9096 31.989C44.2964 32.1979 50.1032 30.4768 48.3209 29.7729C47.7086 29.434 47.1876 29.537 47.0824 30.1563C46.7711 29.0152 45.8277 30.6914 45.2428 30.3744C45.338 29.5426 43.9174 31.4167 44.1295 30.5384C37.6167 30.3944 25.6493 39.724 40.8255 33.2041C39.218 33.0188 40.6554 32.5298 41.3794 32.6287C40.4962 33.7793 43.3258 32.5457 43.8251 32.1261C42.9181 32.2503 44.3277 31.315 43.9065 31.9866L43.9096 31.989Z" />
                    <path d="M31.1742 23.4941C32.0038 23.3481 34.8483 17.2583 35.2257 16.6666C35.7963 15.2515 33.8616 15.3637 33.6797 16.5353C33.5454 16.7474 33.1876 18.4271 32.7181 17.7781C31.123 18.3209 31.2287 21.2383 29.8684 21.5744C30.1331 21.9302 29.6966 22.4398 29.8375 22.6819C29.4217 22.6843 29.2562 23.2605 29.3603 23.2987C27.7702 24.9228 30.1396 26.3595 30.8463 24.2051C30.9274 24.2847 31.3634 23.8695 31.1748 23.4996L31.1742 23.4941Z" />
                    <path d="M8.6666 15.2222C8.02491 16.2514 7.96222 15.2487 8.28065 14.7497C9.05234 15.52 8.82473 13.5428 8.54045 13.0616C8.52243 13.2717 8.17 13.3446 8.18227 12.9105C9.08124 13.2088 9.17211 7.72002 7.35544 10.3155C7.36042 10.4093 8.06672 10.4491 7.84889 10.5596C6.77715 10.3778 7.53675 11.2383 7.62227 11.4811C7.06702 11.477 7.58662 11.707 7.35778 11.7688C6.60614 11.4733 7.8784 12.8725 7.13341 12.5375C6.01834 13.7974 7.55678 16.2818 6.53876 17.2452C7.79737 17.9053 5.99454 21.9334 8.35492 20.3752C8.8673 18.6393 9.14873 17.2179 8.6666 15.2222Z" />
                    <path d="M21.257 4.74268C19.536 4.31212 19.9685 8.08877 19.2823 9.11742C18.2988 10.3626 15.0337 23.7231 18.3519 20.2458C19.306 16.1939 20.7963 13.0387 21.3321 7.97975C21.4791 7.16432 22.4539 4.63025 21.257 4.74268Z" />
                    <path d="M37.0965 44.953C36.1393 46.3589 34.6992 44.6262 33.7861 45.783C32.4207 48.0004 45.1215 48.6579 44.6732 46.3948C44.025 44.501 38.0156 45.7761 37.0965 44.953Z" />
                </svg>
            )}
        </span>
    );
};

export const ConfluenceNewUserWorkspaceNameFormComponent = () => {
    const intl = useIntl();
    const {
        isSubmittingState: [isSubmitting],
        activateSite
    } = useSiteNameContext();

    const subHeaderText = intl.formatMessage({
        id: "BxpUiSignup.nameSite.subHeader.newWorkspaceSubHeader",
        defaultMessage:
            "This is the URL for your team to access Confluence. Most people use their team or company name."
    });

    const siteNameAgreeButton = intl.formatMessage({
        id: "BxpUiSignup.nameSite.continueButton",
        defaultMessage: "Continue"
    });

    return (
        <div css={containerCss} data-testid="workspace-name-form">
            <div css={textContainerStyles}>
                <div css={headerStyles}>
                    <FormattedMessage
                        id="BxpUiSignup.workspaceName.header"
                        defaultMessage="Name your <styled-word>workspace</styled-word>"
                        values={{
                            "styled-word": (node: ReactNode) =>
                                StyledWord(node, intl.locale === "en")
                        }}
                    />
                </div>
                <div css={subHeader}>{subHeaderText}</div>
            </div>
            <Form onSubmit={activateSite} isDisabled={false}>
                {({ formProps, submitting }) => (
                    <form {...formProps} autoComplete="off">
                        <div>
                            <div css={formWrapperCss}>
                                <WorkspaceNameInput />
                            </div>
                            <div css={buttonStyles}>
                                <Adg4Button
                                    isDisabled={submitting || isSubmitting}
                                    isLoading={submitting || isSubmitting}
                                    type="submit"
                                    dataEventProps={{
                                        "data-product-key": "jira",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "siteName",
                                        "data-label": "continue-or-agree",
                                        "data-label-english":
                                            siteNameAgreeButton,
                                        "data-track-event":
                                            "confluence-workspace-name.workspace-name-form.workspace-name"
                                    }}
                                >
                                    {siteNameAgreeButton}
                                </Adg4Button>
                            </div>
                        </div>
                    </form>
                )}
            </Form>
        </div>
    );
};

export const ConfluenceNewUserWorkspaceNameForm = withFeatureExposed(
    "cfind-2409-v2-cobranding-signup",
    "test",
    ConfluenceNewUserWorkspaceNameFormComponent
);

const textContainerStyles = css({
    display: "grid",
    gap: token("space.100")
});

const headerStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: "700",
    fontSize: "32px",
    lineHeight: "38px",
    textAlign: "center"
});

const subHeader = css({
    fontFamily: wacToken("font.charlie.text"),
    color: wacToken("color.primary.black"),
    textAlign: "center",
    fontWeight: "400",
    fontSize: "16px",
    lineHeight: "24px"
});

const privacyPolicyCss = css({
    color: N800,
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    margin: "0.5rem 0 1.5rem"
});

const containerCss = css({
    alignItems: "center",
    color: wacToken("color.primary.black"),
    display: "flex",
    flexDirection: "column"
});

const inputCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div"]: {
        borderRadius: "21px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input"]: {
        paddingInlineStart: "12px !important"
    }
});

const formWrapperCss = css({
    margin: "24px 0px"
});

const buttonStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["button"]: {
        width: "100%"
    }
});

const accentImageSpanCss = css({
    position: "relative",
    display: "inline-block"
});

const accentImageCss = css({
    height: "52px",
    insetBlockStart: "-21px",
    insetInlineEnd: "-33px",
    position: "absolute",
    width: "57px"
});

const logoContainerCss = css({
    marginBlockEnd: "24px"
});
